import type { EditorConfig, LexicalNode, NodeKey, SerializedTextNode, Spread } from 'lexical';

import { addClassNamesToElement } from '@lexical/utils';
import { $applyNodeReplacement, TextNode } from 'lexical';
import { Channel } from '../types';

const slashStyle =
  'margin: 1px 2px; display: inline-block; color: var(--dark-blue-title); border: 1px solid var(--select-divider); border-radius: 43px; padding: 0 4px; line-height: 22px; border: 1px solid rgba(80, 137, 255, 0.3); border-radius: 43px; color: var(--color-blue); font-weight: 500;';

export type SerializedSlashNode = Spread<
  {
    data?: Channel;
  },
  SerializedTextNode
>;

export class SlashNode extends TextNode {
  static getType(): string {
    return 'slash';
  }

  static clone(node: SlashNode): SlashNode {
    return new SlashNode(node.__text, node.__key);
  }

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(text: string, key?: NodeKey, data?: Channel) {
    super(text, key);
    this.data = data;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.style.cssText = slashStyle;
    addClassNamesToElement(element, config.theme.hashtag);
    return element;
  }

  static importJSON(serializedNode: SerializedSlashNode): SlashNode {
    const node = $createSlashNode(serializedNode.text, serializedNode.data!);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): SerializedTextNode {
    return {
      ...super.exportJSON(),
      type: 'slash',
    };
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  isTextEntity(): true {
    return true;
  }
}

/**
 * Generates a SlashNode, which is a string following the format of a / followed by some text, eg. /lexical.
 * @param text - The text used inside the SlashNode.
 * @returns - The SlashNode with the embedded text.
 */
export function $createSlashNode(text = '', data: Channel): SlashNode {
  const slashNode = $applyNodeReplacement(new SlashNode(text, undefined, data));
  slashNode.setMode('segmented').toggleDirectionless();
  return slashNode as SlashNode;
}

/**
 * Determines if node is a SlashNode.
 * @param node - The node to be checked.
 * @returns true if node is a SlashNode, false otherwise.
 */
export function $isSlashNode(node: LexicalNode | null | undefined): node is SlashNode {
  return node instanceof SlashNode;
}
